// loader.js — R7: caricamento ON-DEMAND dei moduli ospiti lazy (design §9.2).
//
// Il manifest servito (server/public/dist/manifest.json, copia generata da
// client/build.mjs accanto ai bundle) porta la chiave `lazyScripts`
// { <module-id>: [{url, v?, defer}...] } che spa-inject IGNORA (legge solo
// scripts[]): al boot ZERO richieste ai file lazy (B2.13). Quando l'utente entra
// in un modulo lazy, loadModule(id):
//  1. fetch del manifest UNA volta (promise-cache; ETag/max-age=0 di
//     express.static ⇒ dopo un deploy il reload prende gli hash freschi);
//  2. inietta gli <script> del gruppo con async=false — esecuzione nell'ORDINE
//     di inserimento (stesso ordine relativo dell'inject eager storico:
//     file del modulo prima, adapter v2 in coda), download in parallelo;
//  3. attende la registrazione su registry (il modulo chiama Workspaces.register
//     al parse: i retry-loop interni degli ospiti non servono ma non disturbano);
//  4. resolve ⇒ il host attiva; reject ⇒ l'outlet mostra errore con Riprova
//     (B2.18). La promise-cache per gruppo si svuota su errore (Riprova =
//     tentativo pulito) ma i FILE già eseguiti restano in cache per-url: mai
//     doppia esecuzione dello stesso script.
//
// Rollback: manifest-only (togliere `lazy` dal loading-order + rebuild) — questo
// file diventa inerte (nessun gruppo in lazyScripts ⇒ reject "non presente",
// ma i moduli tornano eager e registry.has(id) risolve prima).

import { label, moduleLabel } from './glossary.js';

export function createLoader(deps) {
  const win = deps.win;
  const registry = deps.registry;
  const logger = deps.logger || console;
  const manifestUrl = deps.manifestUrl || '/dist/manifest.json';
  const scriptTimeoutMs = deps.scriptTimeoutMs || 15000; // §9.2
  const regTimeoutMs = deps.regTimeoutMs || 8000;        // mandato R7
  const pollMs = deps.pollMs || 100;

  let manifestPromise = null;     // promise-cache del fetch (si svuota su errore)
  const groupPromises = {};       // id gruppo -> promise in corso/riuscita
  const scriptPromises = {};      // url -> promise RIUSCITA (mai re-iniettare un successo)
  const attempted = {};           // id gruppo -> un tentativo è partito: dopo un fallimento
                                  // PARZIALE (modulo registrato ma adapter no) il retry NON
                                  // corto-circuita su registry.has — completa il gruppo

  function fetchGroups() {
    if (!manifestPromise) {
      manifestPromise = win.fetch(manifestUrl)
        .then((r) => {
          if (!r || !r.ok) throw new Error('manifest lazy non disponibile (HTTP ' + (r && r.status) + ')');
          return r.json();
        })
        .then((m) => (m && typeof m.lazyScripts === 'object' && m.lazyScripts) || {});
      manifestPromise.catch(() => { manifestPromise = null; }); // errore di rete: si ritenta alla prossima
    }
    return manifestPromise;
  }

  function injectScript(src) {
    if (scriptPromises[src]) return scriptPromises[src];
    const p = new Promise((resolve, reject) => {
      const doc = win.document;
      const s = doc.createElement('script');
      s.src = src;
      s.async = false; // esecuzione in ordine di inserimento, download parallelo
      const t = win.setTimeout(() => { reject(new Error('timeout di caricamento: ' + src)); }, scriptTimeoutMs);
      s.onload = () => { win.clearTimeout(t); resolve(); };
      s.onerror = () => {
        win.clearTimeout(t);
        try { s.remove(); } catch (e) { /* stub senza remove */ }
        reject(new Error('caricamento fallito: ' + src));
      };
      (doc.head || doc.documentElement).appendChild(s);
    });
    scriptPromises[src] = p;
    p.catch(() => { if (scriptPromises[src] === p) delete scriptPromises[src]; });
    return p;
  }

  function waitRegistered(id) {
    if (registry.has(id)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let waited = 0;
      const t = win.setInterval(() => {
        if (registry.has(id)) { win.clearInterval(t); resolve(); return; }
        waited += pollMs;
        if (waited >= regTimeoutMs) {
          win.clearInterval(t);
          reject(new Error('il modulo "' + id + '" non si è registrato entro ' + regTimeoutMs + 'ms'));
        }
      }, pollMs);
    });
  }

  function srcOf(entry) {
    return entry.url + (entry.v ? '?v=' + entry.v : '');
  }

  function loadModule(id) {
    if (!attempted[id] && registry.has(id)) return Promise.resolve(); // eager già su, mai tentato lazy
    if (groupPromises[id]) return groupPromises[id];
    attempted[id] = true;
    const p = fetchGroups().then((groups) => {
      const group = groups[id];
      if (!Array.isArray(group) || group.length === 0) {
        if (registry.has(id)) return undefined; // eager registrato nel frattempo: niente gruppo, ok
        throw new Error('modulo "' + id + '" non presente tra i lazy del manifest');
      }
      // inserimento in blocco: async=false garantisce l'ordine di esecuzione;
      // i file già riusciti in un tentativo precedente restano in cache per-url
      return Promise.all(group.map((e) => injectScript(srcOf(e)))).then(() => waitRegistered(id));
    });
    groupPromises[id] = p;
    p.catch((e) => {
      if (groupPromises[id] === p) delete groupPromises[id]; // Riprova = tentativo pulito
      // deploy-skew: se il fallimento è un bundle /dist potato da un deploy
      // avvenuto DOPO l'apertura del tab, il manifest in cache punta a hash
      // morti — si invalida anche lui: il Riprova rifetcha gli hash freschi.
      manifestPromise = null;
      logger.error('[shell] lazy-load "' + id + '":', e && e.message ? e.message : e);
    });
    return p;
  }

  // Kill-switch shell_lazy='0' (flags.js): carica subito TUTTI i gruppi — resa
  // "quasi eager" per-browser senza redeploy. Best-effort sui singoli gruppi.
  function preloadAll() {
    return fetchGroups().then((groups) => Promise.all(
      Object.keys(groups).map((id) => loadModule(id).catch((e) => {
        logger.error('[shell] preload "' + id + '":', e && e.message ? e.message : e);
      }))
    ));
  }

  return { loadModule, preloadAll };
}

// ===========================================================================
// createOutlet — stati dell'outlet (design §9.3, B2.18, regole UI 3/4):
// loading (spinner) durante loadModule+attivazione; errore con Riprova + Chiudi
// (due modi di chiusura: bottone, ESC, click sul fondo — mai schermo bianco:
// sotto l'overlay resta la vista corrente). Vanilla: deve funzionare anche
// PRIMA del mount del frame React (restore di boot).
// ===========================================================================
const OUTLET_CSS = [
  '#shell-outlet { position:fixed; inset:0; z-index:1300; display:flex; align-items:center; justify-content:center; background:rgba(15,23,42,0.45); }',
  '#shell-outlet .so-card { background:#fff; color:#1a1f2e; border-radius:12px; padding:26px 30px; min-width:260px; max-width:min(420px,86vw); box-shadow:0 18px 50px -12px rgba(0,0,0,0.35); text-align:center; font-family:system-ui,sans-serif; }',
  '#shell-outlet .so-spin { width:34px; height:34px; margin:0 auto 14px; border-radius:50%; border:3px solid #cffafe; border-top-color:#06b6d4; animation:so-rot .8s linear infinite; }',
  '@keyframes so-rot { to { transform:rotate(360deg); } }',
  '#shell-outlet .so-msg { font-size:15px; font-weight:600; }',
  '#shell-outlet .so-detail { font-size:12px; color:#5a6478; margin-top:8px; word-break:break-word; }',
  '#shell-outlet .so-actions { margin-top:18px; display:flex; gap:10px; justify-content:center; }',
  '#shell-outlet button { font:inherit; font-size:14px; padding:8px 18px; border-radius:8px; cursor:pointer; border:1px solid #cbd2dc; background:#fff; color:#1a1f2e; }',
  '#shell-outlet button.so-retry { background:#06b6d4; border-color:#06b6d4; color:#fff; font-weight:600; }',
].join('\n');

export function createOutlet(win) {
  const doc = win.document;
  let el = null;
  let escHandler = null;

  function ensureStyle() {
    if (doc.getElementById('shell-outlet-style')) return;
    const st = doc.createElement('style');
    st.id = 'shell-outlet-style';
    st.textContent = OUTLET_CSS;
    doc.head.appendChild(st);
  }

  function hide() {
    if (escHandler) { doc.removeEventListener('keydown', escHandler); escHandler = null; }
    if (el && el.parentNode) el.parentNode.removeChild(el);
    el = null;
  }

  function card() {
    ensureStyle();
    hide();
    el = doc.createElement('div');
    el.id = 'shell-outlet';
    el.setAttribute('role', 'status');
    const c = doc.createElement('div');
    c.className = 'so-card';
    el.appendChild(c);
    (doc.body || doc.documentElement).appendChild(el);
    return c;
  }

  // Spinner + nome modulo (glossario B2.17). Il loading non si chiude a mano:
  // esita SEMPRE in successo (hide) o errore (showError) entro i timeout del loader.
  function showLoading(moduleId) {
    const c = card();
    const spin = doc.createElement('div');
    spin.className = 'so-spin';
    const msg = doc.createElement('div');
    msg.className = 'so-msg';
    msg.textContent = label('shell.loader.loading') + moduleLabel(moduleId) + '…';
    c.appendChild(spin);
    c.appendChild(msg);
  }

  // Errore RUMOROSO con Riprova (B2.18) e Chiudi; ESC e click sul fondo chiudono
  // (≥2 modi di chiusura, invariante UI #2 — su mobile: bottone e tap sul fondo).
  function showError(moduleId, err, onRetry) {
    const c = card();
    const msg = doc.createElement('div');
    msg.className = 'so-msg';
    msg.textContent = label('shell.loader.failed') + moduleLabel(moduleId);
    const detail = doc.createElement('div');
    detail.className = 'so-detail';
    detail.textContent = ((err && err.message) ? String(err.message) : String(err || '')) + ' — ' + label('shell.loader.error_hint');
    const actions = doc.createElement('div');
    actions.className = 'so-actions';
    const retry = doc.createElement('button');
    retry.className = 'so-retry';
    retry.textContent = label('shell.loader.retry');
    retry.onclick = () => { hide(); onRetry(); };
    const close = doc.createElement('button');
    close.className = 'so-close';
    close.textContent = label('shell.loader.close');
    close.onclick = hide;
    actions.appendChild(retry);
    actions.appendChild(close);
    c.appendChild(msg);
    c.appendChild(detail);
    c.appendChild(actions);
    el.onclick = (e) => { if (e.target === el) hide(); };
    escHandler = (e) => { if (e.key === 'Escape') hide(); };
    doc.addEventListener('keydown', escHandler);
  }

  return { showLoading, showError, hide };
}
